import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../../design/Icon';

/* ---------- Popover and menu (docs/design/mockups/_build/shell-ui.js VL.popover) ---------- */

export function Popover({ open, onClose, anchor, align = 'left', width, className = '', label, block = false, children }) {
  const wrapRef = useRef(null);
  const popRef = useRef(null);
  const [flip, setFlip] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) onClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  useLayoutEffect(() => {
    if (!open || !popRef.current) {
      setFlip(false);
      return;
    }
    const rect = popRef.current.getBoundingClientRect();
    if (align === 'left' && rect.right > window.innerWidth - 8) setFlip(true);
    const first = popRef.current.querySelector('input,button');
    if (first) first.focus();
  }, [open, align]);

  const right = align === 'right' || flip;
  return (
    <span className="pop-anchor" ref={wrapRef} style={block ? { display: 'block' } : undefined}>
      {anchor}
      {open && (
        <div
          ref={popRef}
          className={['popover', right ? 'right' : '', className].filter(Boolean).join(' ')}
          style={width ? { minWidth: width } : undefined}
          role="dialog"
          aria-label={label}
        >
          {children}
        </div>
      )}
    </span>
  );
}

export function usePopover() {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((v) => !v), []);
  return { open, setOpen, close, toggle };
}

export function MenuTitle({ children }) {
  return <div className="menu-title">{children}</div>;
}

export function MenuSeparator() {
  return <div className="menu-sep" />;
}

export function MenuItem({ icon, on = false, checkable = false, onClick, className = '', children, ...rest }) {
  return (
    <button type="button" className={['menu-item', on ? 'on' : '', className].filter(Boolean).join(' ')} onClick={onClick} {...rest}>
      {icon && <Icon name={icon} />}
      {children}
      {checkable && <Icon name="check" className="check" />}
    </button>
  );
}

/* ---------- Tabs ---------- */

export function Tabs({ tabs, value, onChange, label }) {
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          className="tab"
          aria-selected={tab.id === value ? 'true' : 'false'}
          onClick={() => onChange && onChange(tab.id)}
        >
          {tab.icon && <Icon name={tab.icon} />}
          {tab.label}
          {tab.count != null && <span className="count">{tab.count}</span>}
        </button>
      ))}
    </div>
  );
}

/* ---------- Drawer and dialog ---------- */

function useEscape(open, onClose) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
}

export function Drawer({ open, onClose, title, footer, children }) {
  useEscape(open, onClose);
  if (!open) return null;
  return createPortal(
    <>
      <div className="scrim" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={title}>
        <div className="drawer-head">
          <h2 className="section-title grow">{title}</h2>
          <button type="button" className="btn btn-ghost icon-btn" aria-label="Close" onClick={onClose}>
            <Icon name="x" />
          </button>
        </div>
        <div className="drawer-body">{children}</div>
        {footer && <div className="drawer-foot">{footer}</div>}
      </aside>
    </>,
    document.body
  );
}

export function Dialog({ open, onClose, title, footer, children }) {
  useEscape(open, onClose);
  if (!open) return null;
  return createPortal(
    <>
      <div className="scrim" onClick={onClose} />
      <div className="dialog" role="dialog" aria-modal="true" aria-label={title}>
        <div className="drawer-head">
          <h2 className="section-title grow">{title}</h2>
          <button type="button" className="btn btn-ghost icon-btn" aria-label="Close" onClick={onClose}>
            <Icon name="x" />
          </button>
        </div>
        <div className="drawer-body">{children}</div>
        {footer && <div className="drawer-foot">{footer}</div>}
      </div>
    </>,
    document.body
  );
}

/* ---------- Toast ---------- */

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const seq = useRef(0);

  const showToast = useCallback((message, icon = 'checkCircle') => {
    const id = ++seq.current;
    setToasts((list) => [...list, { id, message, icon }]);
    setTimeout(() => setToasts((list) => list.filter((t) => t.id !== id)), 3200);
  }, []);

  return (
    <ToastContext.Provider value={showToast}>
      {children}
      {toasts.map((t) => (
        <div key={t.id} className="toast" role="status">
          <Icon name={t.icon} />
          <span>{t.message}</span>
        </div>
      ))}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
}
